import { useState, useEffect, useCallback } from 'react';
import { useKeycloak } from '@react-keycloak/web';

export interface Attorney {
  id: string;
  keycloakUserId?: string;
  name: string;
  email: string;
  userType: string;
  roles: string[];
}

export const useAttorneys = () => {
  const { keycloak } = useKeycloak();
  const [attorneys, setAttorneys] = useState<Attorney[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // ✅ SECURITY ENHANCEMENT: JWT Authentication
  const getAuthHeaders = useCallback(() => {
    if (!keycloak?.token) {
      console.warn('🔒 [useAttorneys] No JWT token available for authentication');
      return { 'Content-Type': 'application/json' };
    }
    
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${keycloak.token}`
    };
  }, [keycloak?.token]);

  const fetchAttorneys = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('🔍 [useAttorneys] Fetching attorneys with JWT authentication...');
      
      const headers = getAuthHeaders();
      if (!headers.Authorization) {
        throw new Error('🔒 [Security] JWT token required for attorney data access');
      }
      
      const response = await fetch('/api/v1/users/attorneys', {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch attorneys: ${response.status} ${response.statusText}`);
      }

      const attorneys = await response.json();
      
      console.log('✅ [useAttorneys] Attorneys loaded with JWT auth:', attorneys);
      setAttorneys(attorneys);
      
    } catch (err) {
      console.error('❌ [useAttorneys] Failed to fetch attorneys:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch attorneys');
      
      // Fallback: If no attorneys exist yet, provide empty array instead of mock data
      setAttorneys([]);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    if (keycloak?.authenticated) {
      fetchAttorneys();
    }
  }, [keycloak?.authenticated, fetchAttorneys]);

  const refetch = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const headers = getAuthHeaders();
      if (!headers.Authorization) {
        throw new Error('🔒 [Security] JWT token required for attorney data access');
      }
      
      const response = await fetch('/api/v1/users/attorneys', {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch attorneys: ${response.status} ${response.statusText}`);
      }

      const attorneys = await response.json();
      setAttorneys(attorneys);
      
    } catch (err) {
      console.error('❌ [useAttorneys] Failed to refetch attorneys:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch attorneys');
      setAttorneys([]);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  return {
    attorneys,
    loading,
    error,
    refetch
  };
}; 